import React, { useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';
import IconButton from '@mui/material/IconButton';
import PlayArrowTwoToneIcon from '@mui/icons-material/PlayArrowTwoTone';
import PauseTwoToneIcon from '@mui/icons-material/PauseTwoTone';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import SkipNextTwoToneIcon from '@mui/icons-material/SkipNextTwoTone';
import SkipPreviousTwoToneIcon from '@mui/icons-material/SkipPreviousTwoTone';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import EventTwoToneIcon from '@mui/icons-material/EventTwoTone';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RectangleOverlay from './RectangleOverlay';
import Sparkline from './Sparkline';
import ScenarioSpans from './ScenarioSpans';
import ImageCaptureDialog from './components/ImageCaptureDialog';
import VideoTrimDialog from './components/VideoTrimDialog';
import { Menu, MenuItem, ListItemIcon, ListItemText, Snackbar } from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import { useData } from './DataProvider';

const VideoWithTimeline = ({ src, seekTo, onTimeUpdate, events = [], runId = null, appInfo = null, viewConfig = {}, fullScreen = false }) => {
  const videoRef = useRef(null);
  const aiVideoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [showManualCapture, setShowManualCapture] = useState(false);
  const [showTrimDialog, setShowTrimDialog] = useState(false);
  const [copiedSnackbarOpen, setCopiedSnackbarOpen] = useState(false);
  const {
    setVideoReference,
    setAiVideoReference,
    getNextClickTime,
    getPrevClickTime,
    getNextClickEvent,
    getPrevClickEvent,
    setSelectedIdx,
    displayRows,
    setShowEventDetailsPanel
  } = useData();
  const [prevClickRow, setPrevClickRow] = useState(null);
  const [nextClickRow, setNextClickRow] = useState(null);

  const handlePlayPause = () => {
    if (!videoRef.current) return;
    if (playing) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setPlaying(!playing);
  };

  const handleTimeUpdate = () => {
    setCurrent(videoRef.current.currentTime);
    setPrevClickRow(getPrevClickEvent(videoRef.current.currentTime * 1000));
    setNextClickRow(getNextClickEvent(videoRef.current.currentTime * 1000));
    if (onTimeUpdate) onTimeUpdate(videoRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
      const tMs = videoRef.current.currentTime * 1000;
      setPrevClickRow(getPrevClickEvent(tMs));
      setNextClickRow(getNextClickEvent(tMs));
    }
  };

  const handleSliderChange = (_, value) => {
    setCurrent(value);
    if (videoRef.current) {
      videoRef.current.currentTime = value;
    }
  };

  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleToggleTrimControlsFromMenu = () => {
    handleMenuClose();
    setShowTrimDialog(true);
  };

  const handleCaptureClickImageFromMenu = () => {
    handleMenuClose();
    setShowManualCapture(true);
  };

  const handleCopyLinkAtTime = () => {
    handleMenuClose();
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('t', String(Math.round(current)));
      navigator.clipboard.writeText(url.toString());
      setCopiedSnackbarOpen(true);
    } catch (e) {
      console.warn('Could not copy link', e);
    }
  };

  // Show event details panel for current event
  const handleShowEventDetails = () => {
    if (videoRef.current && displayRows && displayRows.length > 0) {
      const currentTimeMs = videoRef.current.currentTime * 1000;

      // Find the event closest to the current timestamp, preferring earlier events
      let closestEvent = null;
      let smallestTimeDiff = Infinity;

      for (let i = 0; i < displayRows.length; i++) {
        const row = displayRows[i];
        if (typeof row.VideoOffset === 'number') {
          const timeDiff = Math.abs(row.VideoOffset - currentTimeMs);
          
          // If this event has a smaller time difference, use it
          // If time difference is equal, prefer the earlier event (row.VideoOffset <= currentTimeMs)
          if (timeDiff < smallestTimeDiff || 
              (timeDiff === smallestTimeDiff && row.VideoOffset <= currentTimeMs && 
               (closestEvent === null || row.VideoOffset > closestEvent.VideoOffset))) {
            smallestTimeDiff = timeDiff;
            closestEvent = row;
          }
        }
      }

      // If we found a closest event, select it and show details
      if (closestEvent) {
        const eventIndex = displayRows.findIndex(row => row === closestEvent);
        if (eventIndex >= 0) {
          setSelectedIdx(eventIndex);
          setShowEventDetailsPanel(true);
        }
      }
    }
  };  // Navigate to next event and select it
  const handleNextEvent = () => {
    if (videoRef.current && displayRows && displayRows.length > 0) {
      const currentTimeMs = videoRef.current.currentTime * 1000;

      // Find next event in displayRows - add small buffer to avoid staying at current event
      const nextEvent = displayRows.find(row =>
        typeof row.VideoOffset === 'number' && row.VideoOffset > currentTimeMs + 1
      );

      if (nextEvent) {
        const newTime = nextEvent.VideoOffset / 1000.0;
        videoRef.current.currentTime = newTime;
        setCurrent(newTime);

        // Find and select the event in the displayRows
        const eventIndex = displayRows.findIndex(row => row === nextEvent);
        if (eventIndex >= 0) {
          setSelectedIdx(eventIndex);
        }
      }
    }
  };

  // Navigate to previous event and select it
  const handlePrevEvent = () => {
    if (videoRef.current && displayRows && displayRows.length > 0) {
      const currentTimeMs = videoRef.current.currentTime * 1000;

      // Find previous event in displayRows - add small buffer to avoid staying at current event
      const prevEvents = displayRows.filter(row =>
        typeof row.VideoOffset === 'number' && row.VideoOffset < currentTimeMs - 1
      );
      const prevEvent = prevEvents.length > 0 ? prevEvents[prevEvents.length - 1] : null;

      if (prevEvent) {
        const newTime = prevEvent.VideoOffset / 1000.0;
        videoRef.current.currentTime = newTime;
        setCurrent(newTime);

        // Find and select the event in the displayRows
        const eventIndex = displayRows.findIndex(row => row === prevEvent);
        if (eventIndex >= 0) {
          setSelectedIdx(eventIndex);
        }
      }
    }
  };

  // Seek to offset if seekTo changes; sync local state and prev/next so click panel and slider update
  React.useEffect(() => {
    if (typeof seekTo === 'number' && !isNaN(seekTo) && seekTo >= 0) {
      const t = Math.max(0, seekTo);
      if (videoRef.current) {
        videoRef.current.currentTime = t;
      }
      setCurrent(t);
      setPrevClickRow(getPrevClickEvent(t * 1000));
      setNextClickRow(getNextClickEvent(t * 1000));
    }
  }, [seekTo, getPrevClickEvent, getNextClickEvent]);

  // Set video references in DataProvider
  React.useEffect(() => {
    setVideoReference(videoRef);
    setAiVideoReference(aiVideoRef);
  }, [setVideoReference, setAiVideoReference]);

  const videoContainerSx = fullScreen
    ? { width: '100%', flexShrink: 0, overflow: 'hidden', position: 'relative', aspectRatio: '16/9', maxHeight: 'calc(100% - 120px)' }
    : { width: '100%', flexShrink: 1, overflow: 'hidden', position: 'relative', aspectRatio: '16/9', minHeight: 0 };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: fullScreen ? 'auto' : 'hidden', alignItems: 'flex-start', justifyContent: 'flex-start' }}>
      <Box sx={videoContainerSx}>
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <video
            ref={aiVideoRef}
            src={src}
            crossOrigin="anonymous"
            width="100%"
            style={{
              display: 'none',
              transition: 'max-width 0.3s, height 0.3s',
            }}
            controls={false}
          />
          <video
            ref={videoRef}
            src={src}
            crossOrigin="anonymous"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              transition: 'max-width 0.3s, height 0.3s',
            }}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            onPause={() => setPlaying(false)}
            onPlay={() => setPlaying(true)}
            controls={false}
          />
        </Box>
        {/* Rectangle overlay for bounding rectangles */}
        <RectangleOverlay
          events={events}
          currentTime={current}
          videoRef={videoRef}
          displayDuration={5}
          viewConfig={viewConfig}
        />
      </Box>
      {/* Controls: right below video in fullScreen, otherwise at bottom */}
      <Box display="flex" alignItems="flex-start" gap={0} sx={{ flexShrink: 0, mt: 1, px: 1.5, width: '100%' }}>

        {/* Column 1: Slider and Sparkline */}
        <Box flex={1} display="flex" flexDirection="column" alignItems="stretch">
          <Slider
            min={0}
            max={duration}
            value={current}
            step={0.01}
            onChange={handleSliderChange}
            sx={{
              width: '100%',
              '& .MuiSlider-thumb': { width: 12, height: 12 }
            }}
          />
          <Box width="100%" mt={0}>
            {!!viewConfig?.eventsSparkline && <Sparkline />}
            {!!viewConfig?.scenarios && <ScenarioSpans runId={runId} />}
          </Box>


        </Box>

        {/* Column 2: Buttons in 2 rows */}
        <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.5}>

          {/* Button Row 1: Timestamps, Time Navigation, More */}
          <Box display="flex" alignItems="center" gap={1}>
            {/* Timestamps */}
            <Typography variant="subtitle2" sx={{ textAlign: 'right', mr: 1, minWidth: 120 }}>
              {formatTimeWithMilliseconds(current)} / {formatTime(duration)}
            </Typography>

            {/* Time navigation: back/play/forward */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title="Back 1s">
                <IconButton
                  sx={{
                    color: 'primary.main',
                    fontSize: 18,
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                    borderRadius: 2,
                    p: 1,
                    '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.18)' }
                  }}
                  size="large"
                  onClick={() => {
                    if (videoRef.current) {
                      const newTime = Math.max(0, videoRef.current.currentTime - 1);
                      videoRef.current.currentTime = newTime;
                      setCurrent(newTime);
                    }
                  }}
                >
                  <SkipPreviousTwoToneIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                </IconButton>
              </Tooltip>

              {/* Play/Pause Button */}
              <IconButton
                onClick={handlePlayPause}
                size="large"
                sx={{
                  color: 'primary.main',
                  fontSize: 20,
                  backgroundColor: 'rgba(25, 118, 210, 0.15)',
                  borderRadius: 2,
                  p: 1.2,
                  boxShadow: 2,
                  '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.25)' }
                }}
              >
                {playing
                  ? <PauseTwoToneIcon sx={{ fontSize: 24 }} />
                  : <PlayArrowTwoToneIcon sx={{ fontSize: 24 }} />
                }
              </IconButton>

              <Tooltip title="Forward 1s">
                <IconButton
                  sx={{
                    color: 'primary.main',
                    fontSize: 18,
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                    borderRadius: 2,
                    p: 1,
                    '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.18)' }
                  }}
                  size="large"
                  onClick={() => {
                    if (videoRef.current) {
                      const newTime = Math.min(duration, videoRef.current.currentTime + 1);
                      videoRef.current.currentTime = newTime;
                      setCurrent(newTime);
                    }
                  }}
                >
                  <SkipNextTwoToneIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                </IconButton>
              </Tooltip>
            </Box>

            {/* More options */}
            <Tooltip title="More options">
              <IconButton
                sx={{ color: 'primary.main', fontSize: 18, borderRadius: 2, p: 1 }}
                onClick={handleMenuOpen}
                size="large"
              >
                <MoreVertIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Button Row 2: Event, Click, Event Details Navigation */}
          <Box display="flex" alignItems="center" gap={1}>

            <Tooltip title="Back 1 click">
              <IconButton
                sx={{ color: 'secondary.main' }}
                onClick={() => {
                  if (videoRef.current) {
                    const newTime = Math.max(0, getPrevClickTime(videoRef.current.currentTime * 1000)) / 1000.0;
                    videoRef.current.currentTime = newTime;
                    setCurrent(newTime);
                  }
                }}
              >
                <SkipPreviousTwoToneIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Previous Event">
              <IconButton
                sx={{ color: 'primary.main' }}
                onClick={handlePrevEvent}
              >
                <ArrowBackIosIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Show Current Event Details">
              <IconButton
                sx={{ color: 'info.main' }}
                onClick={handleShowEventDetails}
              >
                <EventTwoToneIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Next Event">
              <IconButton
                sx={{ color: 'primary.main' }}
                onClick={handleNextEvent}
              >
                <ArrowForwardIosIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Forward 1 click">
              <IconButton
                sx={{ color: 'secondary.main' }}
                onClick={() => {
                  if (videoRef.current) {
                    const newTime = Math.min(duration * 1000, getNextClickTime((videoRef.current.currentTime + 1) * 1000)) / 1000.0;
                    videoRef.current.currentTime = newTime;
                    setCurrent(newTime);
                  }
                }}
              >
                <SkipNextTwoToneIcon />
              </IconButton>
            </Tooltip>

          </Box>
        </Box>
      </Box>

      {/* Click Information Panel */}
      {!!viewConfig?.clickInformationPanel && (prevClickRow || nextClickRow) && (
        <Box sx={{
          mt: 0.5,
          p: 0.75,
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          borderRadius: 0.5,
          fontSize: '0.7rem',
          display: 'flex',
          justifyContent: 'space-between'
        }}>
          {/* Left side - Previous click */}
          <Box sx={{ flex: '1 1 50%', textAlign: 'left' }}>
            {prevClickRow && (
              <Typography variant="caption" sx={{
                fontSize: '0.7rem',
                lineHeight: 1.2,
                display: 'block'
              }}>
                <Box component="span" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                  ← {formatTime(prevClickRow.VideoOffset / 1000)}
                </Box>
                <Box component="span" sx={{ mx: 0.5, color: 'text.secondary' }}>
                  {prevClickRow.ElementControlType && `[${prevClickRow.ElementControlType}]`} {prevClickRow.ElementControlName}
                  {prevClickRow.WindowTitle && ` "${prevClickRow.WindowTitle}"`}
                </Box>
              </Typography>
            )}
          </Box>

          {/* Right side - Next click */}
          <Box sx={{ flex: '1 1 50%', textAlign: 'right' }}>
            {nextClickRow && (
              <Typography variant="caption" sx={{
                fontSize: '0.7rem',
                lineHeight: 1.2,
                display: 'block'
              }}>
                <Box component="span" sx={{ mx: 0.5, color: 'text.secondary' }}>
                  {nextClickRow.ElementControlType && `[${nextClickRow.ElementControlType}]`} {nextClickRow.ElementControlName}
                  {nextClickRow.WindowTitle && ` "${nextClickRow.WindowTitle}"`}
                </Box>
                <Box component="span" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                  {formatTime(nextClickRow.VideoOffset / 1000)} →
                </Box>
              </Typography>
            )}
          </Box>
        </Box>
      )}

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem onClick={handleCopyLinkAtTime}>
          <ListItemIcon>
            <LinkIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Copy link at current time" />
        </MenuItem>
        {!!viewConfig?.captureImage && (
          <MenuItem onClick={handleCaptureClickImageFromMenu}>
            <ListItemIcon>
              <CameraAltIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Capture Image" />
          </MenuItem>
        )}
        {!!viewConfig?.trimVideo && (
          <MenuItem onClick={handleToggleTrimControlsFromMenu}>
            <ListItemIcon>
              <ContentCutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Trim Video" />
          </MenuItem>
        )}
      </Menu>

      {/* Image Capture Dialog */}
      <ImageCaptureDialog
        open={showManualCapture}
        onClose={() => setShowManualCapture(false)}
        appInfo={appInfo}
        videoRef={videoRef}
        currentTime={current}
      />

      {/* Video Trim Dialog */}
      <VideoTrimDialog
        open={showTrimDialog}
        onClose={() => setShowTrimDialog(false)}
        videoRef={videoRef}
        videoSrc={src}
      />

      <Snackbar
        open={copiedSnackbarOpen}
        autoHideDuration={2000}
        onClose={() => setCopiedSnackbarOpen(false)}
        message="Link copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export function formatTime(sec) {
  if (!sec || isNaN(sec)) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

export function formatTimeWithMilliseconds(sec) {
  if (!sec || isNaN(sec)) return '0:00.000';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  const ms = Math.round((sec % 1) * 1000);
  return `${m}:${s.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
}

export default VideoWithTimeline;
